import type { ChangeEvent, KeyboardEvent } from 'react';
import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  FormHelperText,
  FormLabel,
  Group,
  IconPlusCircle,
  IconTrash,
  Input,
  Select,
  Switch,
  Table,
  Token,
  Typography,
} from '@screentone/core';

import { ConfirmationDialog, ResponsiveLoader } from 'components';
import { useAlert } from 'contexts/alert/useAlert';
import { usePageLock } from 'contexts/page-lock/usePageLock';
import { CollectionParameterDataTypes } from 'data/generated/graphql';
import { CollectionParameterRow, useCollectionParameters } from 'features/collections-settings/useCollectionParameters';
import { useConvertedProperty } from 'hooks';
import { ROLE_TYPE } from 'hooks/roles/types';
import useRequireRoles from 'hooks/roles/useRequireRoles';
import { usePrompt } from 'hooks/useBeforeLeavePage';
import SiteConfigPage from 'pages/site-config/shared/SiteConfigPage';
import styles from './styles.module.scss';

const DATA_TYPES = Object.values(CollectionParameterDataTypes);

const DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  year: '2-digit',
  month: 'numeric',
  day: 'numeric',
});
const TIME_FORMATTER = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
});

const formatTimestamp = (ms: number) => {
  if (!ms) return { date: '', time: '' };
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return { date: String(ms), time: '' };
  return { date: DATE_FORMATTER.format(date), time: TIME_FORMATTER.format(date) };
};

const TimestampCell = ({ ms, title }: { ms: number; title?: string }) => {
  const { date, time } = formatTimestamp(ms);
  if (!date) return null;
  return (
    <div className={styles.timestamp} title={title}>
      <span>{date}</span>
      <span>{time}</span>
    </div>
  );
};

const formatFullTimestamp = (ms: number) => {
  if (!ms) return '';
  const date = new Date(ms);
  return Number.isNaN(date.getTime()) ? String(ms) : date.toLocaleString();
};

type TagInputCellProps = {
  values: string[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
};

const TagInputCell = ({ values, onChange, disabled = false }: TagInputCellProps) => {
  const [inputValue, setInputValue] = useState('');

  const addValue = (raw: string) => {
    const trimmed = raw.trim();
    if (trimmed && !values.includes(trimmed)) {
      onChange([...values, trimmed]);
    }
    setInputValue('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addValue(inputValue);
    } else if (e.key === 'Backspace' && inputValue === '' && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  };

  const removeValue = (index: number) => {
    onChange(values.filter((_, i) => i !== index));
  };

  return (
    <div className={styles.tagInput}>
      {values.map((v, i) => (
        <Token key={v} color="ice" onDismiss={disabled ? undefined : () => removeValue(i)}>
          {v}
        </Token>
      ))}
      <Input
        value={inputValue}
        onChange={(e: ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => addValue(inputValue)}
        placeholder={values.length === 0 ? 'Type + Enter to add' : undefined}
        className={styles.tagInputField}
        disabled={disabled}
      />
    </div>
  );
};

type DraftRow = {
  key: string;
  label: string;
  description: string;
  enabled: boolean;
  deprecated: boolean;
  dataType: CollectionParameterDataTypes;
  value: string[];
};

const EMPTY_DRAFT: DraftRow = {
  key: '',
  label: '',
  description: '',
  enabled: true,
  deprecated: false,
  dataType: CollectionParameterDataTypes.String,
  value: [],
};

function CollectionSettings() {
  useRequireRoles(ROLE_TYPE.ADMIN, ROLE_TYPE.SUPER);

  const publicationKey = useConvertedProperty() ?? '';
  const { parameters, isLoading, error, createParameter, updateParameter, deleteParameter } =
    useCollectionParameters(publicationKey);
  const { alertSuccess, alertError } = useAlert();
  const { state: lockState } = usePageLock();
  const { isReadOnly } = lockState.publishing;

  const [rows, setRows] = useState<CollectionParameterRow[]>([]);
  const [dirtyKeys, setDirtyKeys] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [draft, setDraft] = useState<DraftRow>(EMPTY_DRAFT);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<CollectionParameterRow | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Stable per-content signature so the reconcile effect only runs when the server
  // list actually changes, not on every render (the hook returns a fresh array ref each render).
  const serverSignature = parameters.map((p) => `${p.key}:${p.updatedAt}`).join('|');

  useEffect(() => {
    // Reconcile the local list against the refetched server list without clobbering
    // unsaved edits. Keep the dirty row for a key the server still has, drop keys the
    // server no longer has (deleted elsewhere or here), and append new keys at the
    // server's position.
    setRows((prev) => {
      const prevByKey = new Map(prev.map((r) => [r.key, r]));
      return parameters.map((p) => (dirtyKeys.has(p.key) ? prevByKey.get(p.key) ?? p : p));
    });
    setDirtyKeys((prev) => {
      const serverKeys = new Set(parameters.map((p) => p.key));
      const next = new Set<string>();
      prev.forEach((k) => {
        if (serverKeys.has(k)) next.add(k);
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverSignature, publicationKey]);

  usePrompt('You have unsaved changes. Are you sure you want to leave?', dirtyKeys.size > 0);

  const markDirty = (key: string) => setDirtyKeys((prev) => new Set(prev).add(key));

  const updateRow = (key: string, patch: Partial<CollectionParameterRow>) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
    markDirty(key);
  };

  const handleDataTypeChange = (key: string, next: CollectionParameterDataTypes) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.key !== key) return r;
        if (next === CollectionParameterDataTypes.Enum) {
          return { ...r, dataType: next, value: r.value ?? [] };
        }
        return { ...r, dataType: next, value: undefined };
      })
    );
    markDirty(key);
  };

  const handleSave = async () => {
    if (dirtyKeys.size === 0 || isReadOnly) return;

    const invalidLabels = Array.from(dirtyKeys).filter((key) => {
      const row = rows.find((r) => r.key === key);
      return row && !row.label.trim();
    });
    if (invalidLabels.length > 0) {
      alertError(`Label is required for: ${invalidLabels.join(', ')}`);
      return;
    }

    setIsSaving(true);
    const failures: string[] = [];
    const remaining = new Set(dirtyKeys);

    // eslint-disable-next-line no-restricted-syntax
    for (const key of Array.from(dirtyKeys)) {
      const row = rows.find((r) => r.key === key);
      if (!row) {
        remaining.delete(key);
        continue;
      }
      try {
        // eslint-disable-next-line no-await-in-loop
        await updateParameter({
          key: row.key,
          label: row.label.trim(),
          description: row.description ?? '',
          enabled: row.enabled,
          deprecated: row.deprecated,
          dataType: row.dataType,
          value: row.dataType === CollectionParameterDataTypes.Enum ? row.value ?? [] : undefined,
        });
        remaining.delete(key);
      } catch (err) {
        failures.push(`${key}: ${(err as Error).message}`);
      }
    }

    setDirtyKeys(remaining);
    setIsSaving(false);

    if (failures.length === 0) {
      alertSuccess('Collection parameters saved.');
    } else {
      alertError(`Some rows failed to save:\n${failures.join('\n')}`);
    }
  };

  const openAddModal = () => {
    setDraft(EMPTY_DRAFT);
    setDraftError(null);
    setAddOpen(true);
  };

  const closeAddModal = () => {
    setAddOpen(false);
    setDraft(EMPTY_DRAFT);
    setDraftError(null);
  };

  const handleDraftDataTypeChange = (next: CollectionParameterDataTypes) => {
    setDraft((prev) => ({ ...prev, dataType: next, value: [] }));
  };

  const handleAdd = async () => {
    if (isReadOnly) return;
    const trimmedKey = draft.key.trim();
    const trimmedLabel = draft.label.trim();
    if (!trimmedKey) {
      setDraftError('Key is required.');
      return;
    }
    if (!/^[a-z][a-z0-9_]*$/.test(trimmedKey)) {
      setDraftError('Key must be snake_case (lowercase letters, digits, and underscores only).');
      return;
    }
    if (!trimmedLabel) {
      setDraftError('Label is required.');
      return;
    }
    if (rows.some((r) => r.key === trimmedKey)) {
      setDraftError(`A parameter with key "${trimmedKey}" already exists.`);
      return;
    }

    setIsCreating(true);
    try {
      await createParameter({
        key: trimmedKey,
        label: trimmedLabel,
        description: draft.description.trim() || undefined,
        enabled: draft.enabled,
        deprecated: draft.deprecated,
        dataType: draft.dataType,
        value: draft.dataType === CollectionParameterDataTypes.Enum ? draft.value : undefined,
      });
      alertSuccess(`Parameter "${trimmedKey}" added.`);
      closeAddModal();
    } catch (err) {
      setDraftError((err as Error).message || 'Failed to create parameter.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!pendingDelete || isReadOnly) return;
    setIsDeleting(true);
    try {
      await deleteParameter(pendingDelete.key);
      alertSuccess(`Parameter "${pendingDelete.key}" deleted.`);
      setDirtyKeys((prev) => {
        const next = new Set(prev);
        next.delete(pendingDelete.key);
        return next;
      });
      setPendingDelete(null);
    } catch (err) {
      alertError(`Failed to delete parameter. ${(err as Error).message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  if (error) {
    return <SiteConfigPage title="Collections">Failed to load collection parameters.</SiteConfigPage>;
  }

  if (isLoading) {
    return (
      <SiteConfigPage title="Collections">
        <ResponsiveLoader />
      </SiteConfigPage>
    );
  }

  const headerActions = (
    <>
      <Button
        tertiary
        icon={IconPlusCircle}
        onClick={openAddModal}
        margin={{ horizontal: 'lg' }}
        disabled={isReadOnly}
      >
        Add parameter
      </Button>
      <Button primary onClick={handleSave} disabled={dirtyKeys.size === 0 || isSaving || isReadOnly}>
        {isSaving ? 'Saving…' : 'Save'}
      </Button>
    </>
  );

  return (
    <SiteConfigPage title="Collections" actions={headerActions}>
      <Box className={styles.box}>
        <Box.Content>
          <Typography variant="note">
            Parameters editors can attach to collections in this publication. Deprecated parameters remain visible but
            are read-only.
          </Typography>
          <div className={styles.tableScroll}>
            <Table margin={{ top: 'lg' }} fullWidth>
              <Table.Head>
                <Table.Row>
                <Table.Cell scope="col">Key</Table.Cell>
                <Table.Cell scope="col">Label</Table.Cell>
                <Table.Cell scope="col">Description</Table.Cell>
                <Table.Cell scope="col" align="right" className={styles.shrinkCol}>
                  Enabled
                </Table.Cell>
                <Table.Cell scope="col" align="right" className={styles.shrinkCol}>
                  Deprecated
                </Table.Cell>
                <Table.Cell scope="col" className={styles.dataTypeCol}>
                  Data Type
                </Table.Cell>
                <Table.Cell scope="col">Value</Table.Cell>
                <Table.Cell scope="col" className={styles.shrinkCol}>
                  Updated
                </Table.Cell>
                <Table.Cell scope="col" align="right" className={styles.shrinkCol}>
                  <span className={styles.srOnly}>Actions</span>
                </Table.Cell>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {rows.length === 0 && (
                <Table.Row>
                  <Table.Cell colSpan="9">
                    <Typography variant="note">No collection parameters configured for this publication.</Typography>
                  </Table.Cell>
                </Table.Row>
              )}
              {rows.map((row) => {
                const readOnly = row.deprecated || isReadOnly;
                const isEnum = row.dataType === CollectionParameterDataTypes.Enum;
                return (
                  <Table.Row key={row.key}>
                    <Table.Cell className={styles.keyCell}>
                      <span className={styles.keyText} title={row.key}>
                        {row.key}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <Input
                        value={row.label}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          updateRow(row.key, { label: e.target.value })
                        }
                        disabled={readOnly}
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <Input
                        value={row.description ?? ''}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          updateRow(row.key, { description: e.target.value })
                        }
                        disabled={readOnly}
                      />
                    </Table.Cell>
                    <Table.Cell align="right" className={styles.shrinkCol}>
                      <Switch
                        checked={row.enabled}
                        onChange={() => updateRow(row.key, { enabled: !row.enabled })}
                        disabled={readOnly}
                      />
                    </Table.Cell>
                    <Table.Cell align="right" className={styles.shrinkCol}>
                      <Switch
                        checked={row.deprecated}
                        onChange={() => updateRow(row.key, { deprecated: !row.deprecated })}
                        disabled={isReadOnly}
                      />
                    </Table.Cell>
                    <Table.Cell className={styles.dataTypeCol}>
                      <Select
                        value={row.dataType}
                        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                          handleDataTypeChange(row.key, e.target.value as CollectionParameterDataTypes)
                        }
                        disabled={readOnly}
                      >
                        {DATA_TYPES.map((dt) => (
                          <option key={dt} value={dt}>
                            {dt}
                          </option>
                        ))}
                      </Select>
                    </Table.Cell>
                    <Table.Cell>
                      {isEnum ? (
                        <TagInputCell
                          values={row.value ?? []}
                          onChange={(value) => updateRow(row.key, { value })}
                          disabled={readOnly}
                        />
                      ) : (
                        <Typography variant="note">—</Typography>
                      )}
                    </Table.Cell>
                    <Table.Cell className={styles.shrinkCol}>
                      <TimestampCell
                        ms={row.updatedAt}
                        title={row.createdAt ? `Created ${formatFullTimestamp(row.createdAt)}` : undefined}
                      />
                    </Table.Cell>
                    <Table.Cell align="right" className={styles.shrinkCol}>
                      <Button
                        size="sm"
                        tertiary
                        icon={IconTrash}
                        color="lava"
                        onClick={() => setPendingDelete(row)}
                        disabled={isReadOnly}
                      />
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
          </div>
        </Box.Content>
      </Box>

      {addOpen && (
        <Dialog status="open" onDismiss={closeAddModal}>
          <Dialog.Title>Add collection parameter</Dialog.Title>
          <Dialog.Content>
            <Group gap="md" direction="column" fullWidth>
              <Group gap="xs" direction="column" fullWidth>
                <FormLabel label="Key" labelPosition="top" required fullWidth />
                <Input
                  value={draft.key}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setDraft((prev) => ({ ...prev, key: e.target.value }))
                  }
                  placeholder="e.g. featured"
                />
                <FormHelperText>Used as the identifier when publishing to Allesseh. Must be snake_case (lowercase letters, digits, and underscores).</FormHelperText>
              </Group>
              <Group gap="xs" direction="column" fullWidth>
                <FormLabel label="Label" labelPosition="top" required fullWidth />
                <Input
                  value={draft.label}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setDraft((prev) => ({ ...prev, label: e.target.value }))
                  }
                  placeholder="Human-readable label"
                />
              </Group>
              <Group gap="xs" direction="column" fullWidth>
                <FormLabel label="Description" labelPosition="top" fullWidth />
                <Input
                  value={draft.description}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setDraft((prev) => ({ ...prev, description: e.target.value }))
                  }
                  placeholder="Optional helper text"
                />
                <FormHelperText>Shown to curators under this parameter&apos;s input in the collection editor.</FormHelperText>
              </Group>
              <Group gap="xs" direction="column" fullWidth>
                <FormLabel label="Data type" labelPosition="top" fullWidth />
                <Select
                  value={draft.dataType}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                    handleDraftDataTypeChange(e.target.value as CollectionParameterDataTypes)
                  }
                >
                  {DATA_TYPES.map((dt) => (
                    <option key={dt} value={dt}>
                      {dt}
                    </option>
                  ))}
                </Select>
              </Group>
              {draft.dataType === CollectionParameterDataTypes.Enum && (
                <Group gap="xs" direction="column" fullWidth>
                  <FormLabel label="Options" labelPosition="top" fullWidth />
                  <TagInputCell
                    values={draft.value}
                    onChange={(value) => setDraft((prev) => ({ ...prev, value }))}
                  />
                  <FormHelperText>Type + Enter to add each option users can select from.</FormHelperText>
                </Group>
              )}
              <FormLabel label="Enabled" labelPosition="right">
                <Switch
                  checked={draft.enabled}
                  onChange={() => setDraft((prev) => ({ ...prev, enabled: !prev.enabled }))}
                />
              </FormLabel>
              <FormLabel label="Deprecated" labelPosition="right">
                <Switch
                  checked={draft.deprecated}
                  onChange={() => setDraft((prev) => ({ ...prev, deprecated: !prev.deprecated }))}
                />
              </FormLabel>
              {draftError && <FormHelperText error>{draftError}</FormHelperText>}
            </Group>
          </Dialog.Content>
          <Dialog.Actions>
            <Button secondary onClick={closeAddModal} disabled={isCreating}>
              Cancel
            </Button>
            <Button primary onClick={handleAdd} disabled={isCreating}>
              {isCreating ? 'Adding…' : 'Add'}
            </Button>
          </Dialog.Actions>
        </Dialog>
      )}

      {pendingDelete && (
        <ConfirmationDialog
          title="Remove collection parameter"
          description={`Are you sure you want to remove the "${pendingDelete.key}" parameter? This cannot be undone.`}
          isLoading={isDeleting}
          onClickFunc={handleDeleteConfirm}
          showDialog={(show) => {
            if (!show) setPendingDelete(null);
          }}
          confirmLabel="Remove"
        />
      )}
    </SiteConfigPage>
  );
}

export default CollectionSettings;
